import { useRef, useState } from "react";
import { Search, Users, MessageCircle, Phone, Instagram, ChevronLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { TeamChat } from "@/components/TeamChat";
import { ConversationList } from "@/components/ConversationList";
import { UserAvatar } from "@/components/UserAvatar";
import { useConversations, ensurePushSubscription } from "@/lib/chat";

export default function Chat() {
  const { user } = useAuth();
  const [convs] = useConversations(true);
  const [active, setActive] = useState("team");
  const [search, setSearch] = useState("");
  const [mobileThread, setMobileThread] = useState(false);

  const merged = convs.map((c) => (c.conv === active ? { ...c, unread: 0 } : c));
  const filtered = search
    ? merged.filter((c) => (c.type === "team" ? "team-kanal" : c.name.toLowerCase()).includes(search.toLowerCase()))
    : merged;
  const activeInfo = merged.find((c) => c.conv === active);

  const select = (c) => {
    setActive(c.conv);
    setMobileThread(true);
    ensurePushSubscription();
  };

  // Wischen (links/rechts) auf dem Handy wechselt zwischen Chats
  const touchRef = useRef(null);
  const onTouchStart = (e) => {
    touchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onTouchEnd = (e) => {
    const start = touchRef.current;
    touchRef.current = null;
    if (!start) return;
    const dx = e.changedTouches[0].clientX - start.x;
    const dy = e.changedTouches[0].clientY - start.y;
    if (Math.abs(dx) < 70 || Math.abs(dy) > 60) return;
    const idx = merged.findIndex((c) => c.conv === active);
    const next = dx < 0 ? idx + 1 : idx - 1;
    if (next < 0 || next >= merged.length) return;
    setActive(merged[next].conv);
  };

  return (
    <div className="h-full min-h-0 flex overflow-hidden" data-testid="chat-page">
      {/* Unterhaltungen */}
      <div className={`w-full md:w-72 min-h-0 shrink-0 bg-white border-r border-neutral-200 flex-col ${mobileThread ? "hidden md:flex" : "flex"}`}>
        <div className="px-4 pt-4 pb-3 border-b border-neutral-100">
          <h1 className="font-head font-bold text-lg text-neutral-900 flex items-center gap-2">
            <MessageCircle className="h-5 w-5" /> Chat
          </h1>
          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <input
              data-testid="chat-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Person suchen…"
              className="w-full h-9 pl-9 pr-3 rounded-lg bg-neutral-50 border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            />
          </div>
        </div>
        <ConversationList conversations={filtered} active={active} onSelect={select} meId={String(user?.id)} />
      </div>

      {/* Aktiver Thread */}
      <div
        className={`flex-1 min-w-0 min-h-0 flex-col bg-white ${mobileThread ? "flex" : "hidden md:flex"}`}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-testid="chat-thread-container"
      >
        <div className="min-h-16 shrink-0 border-b border-neutral-200 flex items-center gap-3 px-3 sm:px-5 py-2">
          <button
            data-testid="chat-mobile-back"
            onClick={() => setMobileThread(false)}
            aria-label="Zurück zu den Unterhaltungen"
            className="md:hidden h-11 w-11 -ml-1 rounded-lg hover:bg-neutral-100 flex items-center justify-center text-neutral-600 shrink-0"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          {activeInfo?.type === "dm" ? (
            <>
              <UserAvatar src={activeInfo.user.avatar_url} name={activeInfo.name} color={activeInfo.user.avatar_color} className="h-9 w-9" textClass="text-xs" />
              <div className="leading-tight flex-1 min-w-0">
                <div className="text-sm font-bold text-neutral-900 break-words" data-testid="chat-thread-title">{activeInfo.name}</div>
                <div className="text-[11px] text-neutral-400 flex items-center gap-2.5 flex-wrap">
                  <span>{activeInfo.user.role === "admin" ? "Redaktion" : "Creator"}</span>
                  {activeInfo.user.phone && (
                    <a href={`tel:${activeInfo.user.phone}`} data-testid="chat-contact-phone" className="flex items-center gap-1 hover:text-neutral-600">
                      <Phone className="h-3 w-3" /> {activeInfo.user.phone}
                    </a>
                  )}
                  {activeInfo.user.instagram && (
                    <a href={`https://instagram.com/${activeInfo.user.instagram}`} target="_blank" rel="noreferrer" data-testid="chat-contact-instagram" className="flex items-center gap-1 hover:text-neutral-600">
                      <Instagram className="h-3 w-3" /> @{activeInfo.user.instagram}
                    </a>
                  )}
                  {(activeInfo.user.skills || []).length > 0 && (
                    <span className="truncate">{activeInfo.user.skills.join(" · ")}</span>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <span className="h-9 w-9 rounded-full bg-blue-600 text-white flex items-center justify-center">
                <Users className="h-4 w-4" />
              </span>
              <div className="leading-tight">
                <div className="text-sm font-bold text-neutral-900" data-testid="chat-thread-title">Team-Kanal</div>
                <div className="text-[11px] text-neutral-400">Gesamtes Team · @Name erwähnt jemanden</div>
              </div>
            </>
          )}
        </div>
        <div key={active} className="flex-1 min-h-0 flex flex-col animate-in fade-in slide-in-from-right-4 duration-200">
          <TeamChat conv={active} threadVisible={mobileThread} />
        </div>
      </div>
    </div>
  );
}
